// Paused-campaign detection for calendar events.
//
// A paused campaign's occurrence is shown on the calendar but must read as INACTIVE —
// distinct from a live (pending/running) campaign — so the user can see it sits in their
// schedule without mistaking it for something that will send. Detection is centralized here
// (pure) so the lean-event builder and the event chip agree on exactly one rule.

export interface PausableEventLike {
  isCampaign?: boolean;
  campaignStatus?: string | null;
}

/**
 * True when the event is a campaign occurrence whose source campaign is `paused`. A user
 * message (never a campaign) and a live campaign (pending/running/…) are both false.
 */
export function isPausedCampaignEvent(event: PausableEventLike | null | undefined): boolean {
  return !!event && event.isCampaign === true && event.campaignStatus === 'paused';
}

// A drip's paused state lives on the DRIP (`drip.status === 'paused'`), NOT on its materialized
// step campaigns (which stay `pending`). So a paused drip's calendar occurrences must be detected
// via the drip, then stamped `campaignStatus: 'paused'` so the chip treatment (isPausedCampaignEvent)
// fires. These two pure helpers do exactly that, unit-testable without a store.

/** The set of paused drip ids (as strings) from a list of drip docs. */
export function pausedDripIdSet(drips: Array<{ _id?: unknown; status?: string | null }> | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const d of drips ?? []) {
    if (d && d.status === 'paused') {
      const id = String(d._id ?? '');
      if (id) out.add(id);
    }
  }
  return out;
}

/**
 * Stamp `campaignStatus: 'paused'` on every event that belongs to a paused drip (matched by
 * `dripId`). Mutates + returns the same array (the selector builds it fresh each run). A paused
 * STANDALONE campaign already carries `paused` from its own status and is untouched here.
 */
export function markPausedDripEvents<T extends { dripId?: unknown; campaignStatus?: string }>(
  events: T[],
  pausedDripIds: Set<string>,
): T[] {
  if (pausedDripIds.size) {
    for (const ev of events) {
      if (ev && ev.dripId != null && pausedDripIds.has(String(ev.dripId))) ev.campaignStatus = 'paused';
    }
  }
  return events;
}
